import React, { useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { AccessTime as ClockIcon } from '@mui/icons-material';
import { AppointmentStatus } from '../../types/appointment.types';

interface Props {
  scheduledAt: string;
  status: AppointmentStatus;
}

/** Лічильник оновлюємо раз на хвилину — точність ±60с достатня. */
const TICK_MS = 60_000;

function formatRemaining(ms: number): string {
  const absMs = Math.abs(ms);
  const totalMinutes = Math.floor(absMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days} д ${hours} год`;
  if (hours > 0) return `${hours} год ${minutes} хв`;
  if (minutes > 0) return `${minutes} хв`;
  return 'менше хвилини';
}

/**
 * Лічильник часу до/після зустрічі. Показується лише для pending/confirmed,
 * для cancelled/completed — нічого не рендериться (нерелевантно).
 *
 * Якщо до зустрічі лишається <1 год — підсвічуємо warning кольором; <0 (вже минула,
 * але ще не завершена) — error. Інакше — neutral primary.
 */
export const AppointmentCountdown: React.FC<Props> = ({ scheduledAt, status }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);

  if (status !== AppointmentStatus.PENDING && status !== AppointmentStatus.CONFIRMED) {
    return null;
  }

  const target = new Date(scheduledAt).getTime();
  const diff = target - now;
  const isPast = diff < 0;

  const label = isPast
    ? `Минуло ${formatRemaining(diff)}`
    : `Залишилось ${formatRemaining(diff)}`;

  const color: 'primary' | 'warning' | 'error' = isPast
    ? 'error'
    : diff < 60 * 60 * 1000  // менше години
      ? 'warning'
      : 'primary';

  return (
    <Chip
      icon={<ClockIcon sx={{ fontSize: 16 }} />}
      label={label}
      size="small"
      color={color}
      variant="outlined"
    />
  );
};
